import React, { useEffect, useRef, useState } from "react";
import Slider from "./Slider";
import Footer from "../Footer/Footer";
import Accreditation from "./Accreditation";
import ProductService from "../../Services/products_service";
import { Link, useNavigate } from "react-router-dom";

import capsule from "../../assets/Homepage_slider/capsule.jpg";
import groupimg7 from "../../assets/groupimg7.png";
import groupimg2 from "../../assets/groupimg2.jpg";
import groupimg3 from "../../assets/groupimg3.png";
import Groupimg8 from "../../assets/Groupimg8.jpg";
import groupimg5 from "../../assets/groupimg5.png";
import groupimg6 from "../../assets/groupimg6.png";
import varanousoffice from "../../assets/varanousoffice.jpeg";

import "./HomeComponent.css";
import "./Slider.css";

// Helper function to convert numbers to Unicode subscript characters with larger font size
const toSubscript = (str) => {
  if (!str || typeof str !== "string") {
    return str || "N/A"; // Fallback if the string is invalid
  }

  const subscriptMap = {
    0: "₀",
    1: "₁",
    2: "₂",
    3: "₃",
    4: "₄",
    5: "₅",
    6: "₆",
    7: "₇",
    8: "₈",
    9: "₉",
  };

  // Split the string into parts: letters and numbers (e.g., "C14H10N4O5" -> ["C", "14", "H", "10", "N", "4", "O", "5"])
  const parts = str.match(/[A-Za-z]+|[0-9]+/g) || [str];

  return parts.map((part, index) => {
    // If the part is a number, convert to subscript and wrap in a span with larger font size
    if (/^[0-9]+$/.test(part)) {
      const subscripted = part.replace(
        /[0-9]/g,
        (digit) => subscriptMap[digit] || digit
      );
      return (
        <span key={index} className="text-[23px]">
          {subscripted}
        </span>
      );
    }
    // If the part is a letter, return it as is
    return <span key={index}>{part}</span>;
  });
};

const Home = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // State for search input

  const services = [
    { title: "Discovery Chemistry", img: Groupimg8 },
    { title: "Organic Synthesis", img: groupimg3 },
    { title: "Chiral Synthesis", img: groupimg2 },
    { title: "In Silico Research", img: groupimg7 },
    { title: "Custom Quote", img: groupimg5 },
    { title: "Our Facilities", img: groupimg6 },
  ];

  const cardsRef = useRef([]);
  const boxesRef = useRef([]);

  // Function to fetch products by slug using ProductService
  const fetchProductsBySlug = async (slug) => {
    try {
      const response = await ProductService.fetchProductsBySlug(slug);
      if (response.success) {
        return Array.isArray(response.data) ? response.data : [response.data];
      } else {
        console.error("Error fetching products:", response.message);
        return [];
      }
    } catch (err) {
      console.error("Error fetching product by slug:", err);
      return [];
    }
  };

  // Handle search input change
  const handleSearchInputChange = async (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.trim() === "") {
      // Re-fetch featured products when search is cleared
      fetchFeaturedProducts();
      return;
    }

    setLoadingProducts(true);
    setFetchError(null);
    try {
      const response = await ProductService.searchProducts(query);
      if (response.success) {
        const products = response.data.map((product) => ({
          name: product.name,
          img: product.image,
          category: product.categorySlug || "unknown",
          details: {
            "SZ CAT No": product.catalogNumber || "N/A",
            "CAS No": product.casNumber || "N/A",
            "Mol.F": product.molecularFormula || "N/A",
            "Mol.Wt.": product.molecularWeight || "N/A",
            "Inv. Status": product.status || "N/A",
            "Rel. CAS No": product.relatedCasNumber || "N/A",
          },
        }));
        setFeaturedProducts(products);
        if (products.length === 0) {
          setFetchError("No products found for the search query.");
        }
      } else {
        setFetchError(response.message);
        setFeaturedProducts([]);
      }
    } catch (err) {
      setFetchError(err.message || "Something went wrong while searching.");
      setFeaturedProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Fetch featured products (one per category for A-Z)
  const fetchFeaturedProducts = async () => {
    setLoadingProducts(true);
    setFetchError(null);
    const alphabet = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];
    const productsToDisplay = [];

    try {
      for (const letter of alphabet) {
        const categories = await ProductService.getCategoriesByAlpabet(
          letter.toLowerCase()
        );
        if (categories && categories.length > 0) {
          const firstCategory = categories[0];
          const slug = firstCategory.slug || firstCategory.categorySlug;
          if (!slug) {
            continue;
          }

          // Fetch products for the category slug
          const products = await fetchProductsBySlug(slug);
          let product = products.length > 0 ? products[0] : null;

          if (product) {
            productsToDisplay.push({
              name: product.name,
              img: product.image,
              category: slug,
              details: {
                "SZ CAT No": product.catalogNumber || "N/A",
                "CAS No": product.casNumber || "N/A",
                "Mol.F": product.molecularFormula || "N/A",
                "Mol.Wt.": product.molecularWeight || "N/A",
                "Inv. Status": product.status || "N/A",
                "Rel. CAS No": product.relatedCasNumber || "N/A",
              },
            });
          }
        }

        if (productsToDisplay.length >= 5) break; // Limit to 5 products
      }

      setFeaturedProducts(productsToDisplay);
      if (productsToDisplay.length === 0) {
        setFetchError("No products found across all categories.");
      }
    } catch (error) {
      setFetchError(error.message);
      setFeaturedProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((card) => card && observer.observe(card));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal");
          }
        });
      },
      { threshold: 0.1 }
    );

    boxesRef.current.forEach((box) => box && observer.observe(box));

    return () => {
      boxesRef.current.forEach((box) => box && observer.unobserve(box));
    };
  }, []);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          return;
        }

        const response = await fetch(
          "http://94.136.187.130:8080/varanouslabs/api/customer/myDetails",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setUser(data.responseBody);
      } catch (error) {}
    };

    fetchUserDetails();
  }, []);

  return (
    <div className="bg-[#fef7ff]">
      <Slider />

      {/* Search Input */}
      {/* <div style={{ padding: '20px', textAlign: 'center' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchInputChange}
          placeholder="Search products..."
          style={{ padding: '10px', width: '300px', marginBottom: '20px' }}
        />
      </div> */}

      <section className="home-container">
        <h1 className="home-title">WELCOME TO VARANOUS LABS</h1>
        <p className="home-subtitle">
          We are a chemistry driven contract research organization catering both
          domestic and international customers. We are specialized in the
          synthesis of Advanced drug intermediates across a wide spectrum of
          chemistry type.
        </p>

        <div className="grid-container pt-10">
          {services.map((service, idx) => (
            <div
              key={idx}
              className="card"
              ref={(el) => (cardsRef.current[idx] = el)}
            >
              <img src={service.img} alt={service.title} />
              <div className="overlay12">{service.title}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="home-about-section">
        <div className="overlay"></div>
        <div className="homeabout-content">
          <h2 className="section-title bounce-on-hover">
            Welcome to Varanous Labs
          </h2>
          <p className="section-description">
            Varanous Labs Pvt Ltd is a premium contract research organization
            providing cost effective chemistry solutions to global customers of
            diverse origin varying from, Pharmaceutical, Agrochemicals, Biotech
            companies, and Academic Researchers. Our values are Respect, Team
            effort, Transparency, Integrity, Confidentiality, and Safety. We are
            a socially responsible and environmentally conscious research
            organization. We pride ourselves on delivering innovative and
            reliable research services tailored to the specific needs of our
            clients. With a team of experienced scientists and state-of-the-art
            infrastructure, we ensure high-quality results and accelerated
            project timelines. Varanous Labs is also deeply committed to
            sustainability—we operate with a strong sense of environmental
            responsibility and uphold the highest standards in workplace safety
            and ethical research practices.
            <br />
            <br />
            Partner with us to turn your scientific ideas into real-world
            solutions.
          </p>
        </div>
      </section>

      <section className="featured-products overflow-hidden py-6">
        <h2 className="text-2xl font-semibold text-center mb-4 text-white">
          {searchQuery ? "Search Results" : "Featured Products"}
        </h2>

        {loadingProducts ? (
          <div className="text-center">Loading products...</div>
        ) : fetchError ? (
          <div className="text-center text-red-500">{fetchError}</div>
        ) : featuredProducts.length > 0 ? (
          <div className="scroll-wrapper group relative overflow-hidden">
            <div className="scroll-content group-hover:[animation-play-state:paused] flex">
              {featuredProducts.concat(featuredProducts).map((product, idx) => (
                <Link
                  key={idx}
                  to={`/product-detail/${product.details["SZ CAT No"]}`}
                  className="product-card mx-3 w-[280px] bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col hover:shadow-xl transition-shadow"
                >
                  {/* Header with product name */}
                  <div className="bg-teal-700 text-white text-center p-3 font-semibold text-sm truncate">
                    {product.name}
                  </div>

                  {/* Image section */}
                  <div className="flex items-center justify-center p-4 bg-white h-28">
  {product.img ? (
    <img
      src={product.img}
      alt={product.name}
      className="h-28 object-contain"
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  ) : (
    <div >
      No Image 
    </div>
  )}
</div>


                  {/* Details section */}
                  <div className="flex-1 bg-[#2b6e73] text-white text-xs text-left w-full p-4">
                    <div className="space-y-2">
                      {Object.entries(product.details)
                        .filter(
                          ([key]) =>
                            key !== "Inv. Status" && key !== "Rel. CAS No"
                        )
                        .map(([key, value]) => {
                          const isMolecularFormula = key === "Mol.F";
                          return (
                            <div
                              key={key}
                              className={`flex items-start ${
                                isMolecularFormula ? "text-sm" : "text-xs"
                              }`}
                            >
                              <span className="w-[95px] font-medium">
                                {key}
                              </span>
                              <span className="w-2">:</span>
                              <span className="flex-1 break-words">
                                {isMolecularFormula
                                  ? toSubscript(value)
                                  : value}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center">No products found.</div>
        )}
      </section>

      <section className="business-offerings">
        <div
          className="business-img-container"
          ref={(el) => (boxesRef.current[2] = el)}
        >
          <img src={varanousoffice} alt="Building" />
        </div>
        <div
          className="business-text-container"
          ref={(el) => (boxesRef.current[3] = el)}
        >
          <h3>What We Do</h3>
          <h2>OUR BUSINESS OFFERINGS</h2>
          <p>
            In this effort, we offer leading edge organic synthesis methodology
            and our proprietary technologies in bioinformatics, virtual
            screening and structure-based design.
          </p>
        </div>
      </section>

      <Accreditation />
      <Footer />
    </div>
  );
};

export default Home;
